import type { BackgroundAttributes } from '@redfox-thank-you/control-groups';
import { createAttributeManager, StyleRenderer } from '@redfox-thank-you/utils';
import { RichText, useBlockProps } from '@wordpress/block-editor';
import { __ } from '@wordpress/i18n';
import classNames from 'classnames';
import React from 'react';

import { Settings } from './settings';
import { generateCustomerDetailsCSS } from './style';
import type { CustomerDetailsEditProps } from './types';

/**
 * Customer Details block edit component.
 */
export const CustomerDetailsEdit: React.FC< CustomerDetailsEditProps > = ( {
	attributes,
	setAttributes,
} ) => {
	// Create attribute manager with utility functions
	const { updateAttributes, getAttribute } = createAttributeManager(
		attributes,
		setAttributes,
	);

	// Extract settings properties with defaults using type-safe methods
	const title = getAttribute< string >(
		'settings.title.text',
		__( 'Customer Details', 'redfox-thank-you' ),
	);
	const titleLevel = getAttribute< number >( 'settings.title.level', 2 );
	const showTitle = getAttribute< boolean >( 'settings.title.show', true );

	const showCustomerName = getAttribute< boolean >(
		'settings.display.customerName',
		true,
	);
	const showCustomerEmail = getAttribute< boolean >(
		'settings.display.customerEmail',
		true,
	);
	const showCustomerPhone = getAttribute< boolean >(
		'settings.display.customerPhone',
		true,
	);
	const showBillingAddress = getAttribute< boolean >(
		'settings.display.billingAddress',
		true,
	);
	const showDeliveryAddress = getAttribute< boolean >(
		'settings.display.deliveryAddress',
		true,
	);
	const tableStyleEnabled = getAttribute< boolean >(
		'settings.layout.tableStyle',
		false,
	);

	// Generate unique block class based on attributes
	const generateBlockClass = () => {
		const cleanName = 'redfox-thank-you-customer-details';
		const hash = Math.random().toString( 36 ).substring( 2, 10 );
		return `redfox-thank-you-${ cleanName }-${ hash }`;
	};

	const [ blockClass ] = React.useState( () => generateBlockClass() );

	const blockProps = useBlockProps( {
		className: blockClass,
		style: {
			position: 'relative', // Ensure overlay positioning works correctly
		},
	} );

	// Check if any overlay is enabled across responsive breakpoints
	const hasAnyOverlayEnabled = (
		backgroundStyle: BackgroundAttributes,
	): boolean => {
		// Check responsive structure
		const breakpoints = [ 'desktop', 'tablet', 'mobile' ] as const;
		return breakpoints.some( ( breakpoint ) => {
			const breakpointValues = backgroundStyle?.[ breakpoint ];
			return breakpointValues?.backgroundOverlay?.enabled;
		} );
	};

	// Extract background styles for overlay rendering using attribute manager
	const blockBackgroundStyle = getAttribute< BackgroundAttributes >(
		'style.block.background',
	);

	// Check if overlay is enabled
	const overlayEnabled = hasAnyOverlayEnabled( blockBackgroundStyle );

	return (
		<>
			<StyleRenderer
				css={ generateCustomerDetailsCSS( attributes, blockClass ) }
				blockId={ blockClass }
			/>
			<Settings
				updateAttributes={ updateAttributes }
				getAttribute={ getAttribute }
			/>

			<div { ...blockProps }>
				{ /* Background Overlay - responsive aware */ }
				{ overlayEnabled && (
					<div
						className="background-overlay" // Don't prefix this class with blockClass to ensure it applies globally.
					/>
				) }

				{ showTitle && (
					<RichText
						tagName={ `h${ titleLevel }` }
						className="redfox-thank-you-customer-details__title"
						value={ title }
						onChange={ ( value: string ) =>
							updateAttributes( 'settings.title.text', value )
						}
						placeholder={ __(
							'Customer Details',
							'redfox-thank-you',
						) }
						allowedFormats={ [ 'core/bold', 'core/italic' ] }
					/>
				) }

				<div
					className={ classNames( {
						'redfox-thank-you-customer-details__content': true,
						'redfox-thank-you-customer-details__content--table-style':
							tableStyleEnabled,
					} ) }
				>
					{ showCustomerName && (
						<div className="redfox-thank-you-customer-details__item">
							<strong className="redfox-thank-you-customer-details__label">
								{ __( 'Customer:', 'redfox-thank-you' ) }
							</strong>
							<span className="redfox-thank-you-customer-details__value">
								{ __( 'John Doe', 'redfox-thank-you' ) }
							</span>
						</div>
					) }

					{ showCustomerEmail && (
						<div className="redfox-thank-you-customer-details__item">
							<strong className="redfox-thank-you-customer-details__label">
								{ __( 'E-mail:', 'redfox-thank-you' ) }
							</strong>
							<span className="redfox-thank-you-customer-details__value">
								{ __( 'john@example.com', 'redfox-thank-you' ) }
							</span>
						</div>
					) }

					{ showCustomerPhone && (
						<div className="redfox-thank-you-customer-details__item">
							<strong className="redfox-thank-you-customer-details__label">
								{ __( 'Phone:', 'redfox-thank-you' ) }
							</strong>
							<span className="redfox-thank-you-customer-details__value">
								{ __( '123-456-7890', 'redfox-thank-you' ) }
							</span>
						</div>
					) }

					{ showBillingAddress && (
						<div className="redfox-thank-you-customer-details__item redfox-thank-you-customer-details__item--address">
							<strong className="redfox-thank-you-customer-details__label">
								{ __( 'Billing address:', 'redfox-thank-you' ) }
							</strong>
							<span className="redfox-thank-you-customer-details__value">
								{ __(
									'123 Example Street, City, State 12345, United States',
									'redfox-thank-you',
								) }
							</span>
						</div>
					) }

					{ showDeliveryAddress && (
						<div className="redfox-thank-you-customer-details__item redfox-thank-you-customer-details__item--address">
							<strong className="redfox-thank-you-customer-details__label">
								{ __(
									'Delivery address:',
									'redfox-thank-you',
								) }
							</strong>
							<span className="redfox-thank-you-customer-details__value">
								{ __(
									'456 Delivery Lane, City, State 67890, United States',
									'redfox-thank-you',
								) }
							</span>
						</div>
					) }
				</div>
			</div>
		</>
	);
};
